/**
 * Vue directive for scroll reveal animations
 * Usage: v-scroll-reveal="{ animation: 'fade-up', delay: 200 }"
 */

let observer = null;
const animatedElements = new WeakSet();

// Initialize intersection observer
const initObserver = () => {
  if (typeof window === 'undefined' || !window.IntersectionObserver) {
    return null;
  }

  return new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !animatedElements.has(entry.target)) {
          const element = entry.target;
          const config = element._scrollRevealConfig || {};
          
          // Add delay if specified
          const delay = config.delay || 0;
          
          setTimeout(() => {
            triggerAnimation(element, config);
            animatedElements.add(element);
            
            // Unobserve if once is true (default)
            if (config.once !== false) {
              observer?.unobserve(element);
            }
          }, delay);
        }
      });
    },
    {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    }
  );
};

// Trigger animation on element
const triggerAnimation = (element, config) => {
  const animation = config.animation || 'fade-up';
  
  // Remove initial hidden classes
  element.classList.remove('opacity-0', 'translate-y-8', 'translate-x-8', '-translate-x-8', 'scale-90');
  
  // Add animation class
  element.classList.add(`animate-scroll-${animation}`);
  
  // Dispatch custom event
  element.dispatchEvent(new CustomEvent('scroll-revealed', {
    detail: { element, animation, config }
  }));
};

// Setup element for animation
const setupElement = (element, config) => {
  const animation = config.animation || 'fade-up';
  
  // Store config on element
  element._scrollRevealConfig = config;
  
  // Add initial classes based on animation type
  element.classList.add('scroll-optimized');
  
  switch (animation) {
    case 'fade':
      element.classList.add('opacity-0');
      break;
    case 'fade-up':
    case 'up':
      element.classList.add('opacity-0', 'translate-y-8');
      break;
    case 'fade-down':
    case 'down':
      element.classList.add('opacity-0', '-translate-y-8');
      break;
    case 'fade-left':
    case 'left':
      element.classList.add('opacity-0', 'translate-x-8');
      break;
    case 'fade-right':
    case 'right':
      element.classList.add('opacity-0', '-translate-x-8');
      break;
    case 'scale':
      element.classList.add('opacity-0', 'scale-90');
      break;
    default:
      element.classList.add('opacity-0', 'translate-y-8');
  }
  
  // Add transition classes
  element.classList.add('transition-all', 'duration-700', 'ease-out');
};

// Vue directive definition
export const scrollReveal = {
  mounted(el, binding) {
    // Initialize observer if not already done
    if (!observer) {
      observer = initObserver();
    }
    
    if (!observer) return;
    
    // Parse binding value
    const config = typeof binding.value === 'object' ? binding.value : {};
    if (typeof binding.value === 'string') {
      config.animation = binding.value;
    }
    
    // Setup element
    setupElement(el, config);
    
    // Start observing
    observer.observe(el);
  },
  
  unmounted(el) {
    if (observer) {
      observer.unobserve(el);
    }
    animatedElements.delete(el);
  },
  
  updated(el, binding) {
    // Handle config updates
    const config = typeof binding.value === 'object' ? binding.value : {};
    if (typeof binding.value === 'string') {
      config.animation = binding.value;
    }
    
    el._scrollRevealConfig = config;
  }
};

// Plugin installation function
export default {
  install(app) {
    app.directive('scroll-reveal', scrollReveal);
  }
};

// Utility functions for manual control
export const scrollRevealUtils = {
  // Manually trigger animation on element
  reveal(element, animation = 'fade-up') {
    if (!element) return;
    
    const config = { animation, once: false };
    setupElement(element, config);
    
    // Trigger immediately
    setTimeout(() => {
      triggerAnimation(element, config);
    }, 50);
  },
  
  // Reset element to initial state
  reset(element) {
    if (!element) return;
    
    const config = element._scrollRevealConfig || {};
    const animation = config.animation || 'fade-up';
    
    // Remove animation classes
    element.classList.remove(`animate-scroll-${animation}`);
    
    // Reset to initial state
    setupElement(element, config);
    animatedElements.delete(element);
  },
  
  // Check if element has been animated
  isRevealed(element) {
    return animatedElements.has(element);
  }
};